import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";

import {
  customLabels,
  fieldMapping,
} from "../../../../../../../../data/buildingPermit/fieldDatas";

const formatNumber = (input) => {
  const digits = input.replace(/\D/g, "").slice(0, 10);
  const parts = [];

  if (digits.length > 0) parts.push(digits.slice(0, 3));
  if (digits.length > 3) parts.push(digits.slice(3, 6));
  if (digits.length > 6) parts.push(digits.slice(6));

  return parts.join("-");
};

const DESIGN_PRO_FIELD_NAMES = [
  "design_pro_address",
  "design_pro_company",
  "design_pro_email",
  "design_pro_fax",
  "design_pro_license_name",
  "design_pro_license_num",
  "design_pro_phone",
];

const RenderDesignPro = ({ full_title, designPro, onChange, fields }) => {
  const hasValidatedOnce = useRef(false);

  const currentSession = useSelector((state) => state.sessions.currentSession);
  const currentUser = useSelector((state) => state[currentSession]?.user);
  const savedDesignPro = currentUser?.design_professionals;
  const { createDocLoading, addingNewDesignPro } = useSelector(
    (state) => state.buildingState,
  );

  const [designProList, setDesignProList] = useState([]);
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    setDesignProList(savedDesignPro || []);
    setLoadingData(false);
  }, [savedDesignPro]);

  // Index fields by name once per render — O(1) lookups below
  const fieldMap = useMemo(
    () => Object.fromEntries((fields || []).map((f) => [f.name, f])),
    [fields],
  );

  useEffect(() => {
    // Run only if we haven’t validated AND projectList has loaded
    if (!hasValidatedOnce.current && designProList.length > 0 && designPro) {
      const selectedId = designPro?.value;
      const selected = designProList.find((pro) => pro.id === selectedId);

      if (!selected) {
        onChange("designProfessional", "");
        DESIGN_PRO_FIELD_NAMES.forEach((name) => onChange(name, ""));
      } else {
        const phone = fieldMap["design_pro_phone"]?.value;
        const fax = fieldMap["design_pro_fax"]?.value;

        if (phone && !phone.includes("-")) {
          const formatted = formatNumber(phone);
          onChange("design_pro_phone", formatted);
        }
        if (fax && !fax.includes("-")) {
          const formatted = formatNumber(fax);
          onChange("design_pro_fax", formatted);
        }
      }

      hasValidatedOnce.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designProList, designPro]);

  useEffect(() => {
    const selectedId = designPro?.value;
    const isValid = designProList.some((pro) => pro.id === selectedId);
    if (!isValid && selectedId) {
      onChange("designProfessional", "");
      DESIGN_PRO_FIELD_NAMES.forEach((name) => onChange(name, ""));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designProList]);

  const handleSelectChange = (event) => {
    const selectedId = event.target.value;
    const selected = designProList.find((p) => p.id === selectedId);

    onChange("designProfessional", {
      value: selectedId,
      label: selected?.companyName,
    });

    onChange("design_pro_address", selected?.address || "");
    onChange("design_pro_company", selected?.companyName || "");
    onChange("design_pro_email", selected?.email || "");
    onChange("design_pro_fax", selected?.fax || "");
    onChange("design_pro_license_name", selected?.licenseName || "");
    onChange("design_pro_license_num", selected?.licenseNumber || "");
    onChange("design_pro_phone", selected?.phone || "");
  };

  const selectedPro = designProList?.find((pro) => pro.id === designPro?.value);

  if (loadingData) {
    return <Skeleton variant="rectangular" height={120} />;
  }

  return (
    <>
      {full_title && (
        <Typography align="center" mt={2} mb={0} variant="h6">
          {full_title}
        </Typography>
      )}

      <FormControl
        fullWidth
        margin="normal"
        variant="filled"
        sx={{
          "& .MuiFilledInput-root": {
            border: designPro ? "2px solid #004976" : undefined, // adjust thickness and color
            borderRadius: "14px",
            backgroundColor: "transparent", // optional
          },
          "& .MuiFilledInput-underline:before": {
            borderBottom: "none", // remove default bottom border
          },
          "& .MuiFilledInput-underline:after": {
            borderBottom: "none",
          },
        }}
      >
        <InputLabel>Select Design Professional</InputLabel>
        <Select
          disabled={createDocLoading || addingNewDesignPro}
          name="designProfessional"
          value={designPro?.value || ""}
          onChange={handleSelectChange}
          MenuProps={{
            PaperProps: {
              style: {
                marginTop: "1.5px",
                maxHeight: 200,
                width: "auto",
                maxWidth: "100%",
              },
            },
          }}
        >
          <MenuItem disabled value="">
            {designProList.length === 0 ? "No records found" : "Select One"}
          </MenuItem>
          {designProList?.map((option, i) => (
            <MenuItem key={option.id + i} value={option.id}>
              {option.companyName || "Unnamed"}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {designPro && selectedPro && (
        <>
          {Object.keys(fieldMapping)?.map((key) => (
            <TextField
              key={key}
              fullWidth
              disabled={!addingNewDesignPro}
              margin="normal"
              variant="filled"
              label={customLabels[key]}
              name={key}
              value={
                key === "design_pro_company"
                  ? designPro?.label
                  : fieldMap[key]?.value || "" || ""
              }
            />
          ))}
        </>
      )}
    </>
  );
};

export default RenderDesignPro;
